/**
 * useSpotifyAPI.js
 *
 * Extracted from PlayerContext.js - handles authenticated Spotify API requests
 * with automatic token refresh and retry logic.
 *
 * Provides: makeAuthenticatedRequest, getValidToken
 */
import { useCallback } from 'react';
import logger from '../services/logger';

export function useSpotifyAPI(state, dispatch, refs) {
  const { getValidToken, refreshSpotifyToken, refreshYouTubeToken } = refs;

  /**
   * Enhanced API call with automatic token refresh
   */
  const makeAuthenticatedRequest = useCallback(async (url, options = {}, platform = 'spotify') => {
    const maxRetries = 2;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        // Get fresh token
        const token = await getValidToken(platform);

        if (!token) {
          throw new Error(`No valid ${platform} token available`);
        }

        // Make API call with fresh token
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // If successful, return response
        if (response.ok) {
          return response;
        }

        // If token expired (401), refresh and retry
        if (response.status === 401 && attempt < maxRetries - 1) {
          logger.warn(`🔄 ${platform} token expired, refreshing and retrying...`);

          // Force refresh token
          if (platform === 'spotify') {
            await refreshSpotifyToken();
          } else if (platform === 'youtube') {
            await refreshYouTubeToken();
          }

          attempt++;
          continue;
        }

        // Other error, throw it
        throw new Error(`API call failed: ${response.status} ${response.statusText}`);

      } catch (error) {
        if (attempt === maxRetries - 1) {
          logger.error(`❌ API call failed after ${maxRetries} attempts:`, error);
          throw error;
        }
        attempt++;
      }
    }
  }, [getValidToken, refreshSpotifyToken, refreshYouTubeToken]);

  return {
    makeAuthenticatedRequest,
    getValidToken
  };
}
